import { useState, useEffect, useMemo } from 'react';
import { Plus, Pencil, Trash2, DollarSign, Search, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { getRoutePrices, createRoutePrice, updateRoutePrice, deleteRoutePrice, getZones } from '../lib/api';

const emptyRoute = {
  origin_id: '',
  destination_id: '',
  price: 0,
  active: true
};

export default function AdminRoutePrices() {
  const [routes, setRoutes] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoute, setEditingRoute] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState(emptyRoute);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOrigin, setFilterOrigin] = useState('all');

  const zoneMap = useMemo(() => {
    const map = {};
    zones.forEach(z => { map[z.id] = z; });
    return map;
  }, [zones]);

  const origins = useMemo(() => zones.filter(z => z.zone_type === 'airport' || z.zone_type === 'port'), [zones]);
  const destinations = useMemo(() => zones.filter(z => z.zone_type === 'destination'), [zones]);

  const filteredRoutes = useMemo(() => {
    return routes.filter(r => {
      const originName = zoneMap[r.origin_id]?.name || '';
      const destName = zoneMap[r.destination_id]?.name || '';
      const matchesSearch = !searchTerm || 
        originName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        destName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterOrigin === 'all' || r.origin_id === filterOrigin;
      return matchesSearch && matchesFilter;
    });
  }, [routes, searchTerm, filterOrigin, zoneMap]);

  const fetchData = async () => {
    try {
      const [routesRes, zonesRes] = await Promise.all([getRoutePrices(), getZones()]);
      setRoutes(routesRes.data);
      setZones(zonesRes.data);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setFormData(emptyRoute);
    setEditingRoute('new');
  };

  const openEdit = (route) => {
    setFormData({ origin_id: route.origin_id, destination_id: route.destination_id, price: route.price, active: route.active });
    setEditingRoute(route.id);
  };

  const handleSave = async () => {
    if (!formData.origin_id || !formData.destination_id) {
      toast.error('Selecciona origen y destino');
      return;
    }
    try {
      if (editingRoute === 'new') {
        await createRoutePrice(formData);
        toast.success('Precio de ruta creado');
      } else {
        await updateRoutePrice(editingRoute, formData);
        toast.success('Precio de ruta actualizado');
      }
      setEditingRoute(null);
      fetchData();
    } catch (error) {
      toast.error('Error guardando precio de ruta');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteRoutePrice(deleteId);
      toast.success('Precio de ruta eliminado');
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error('Error eliminando precio de ruta');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Cargando precios de rutas...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-route-prices">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white lg:hidden">Precios de Rutas</h1>
          <span className="text-sm text-slate-500 bg-white/5 px-3 py-1 rounded-full">
            {filteredRoutes.length} rutas
          </span>
        </div>
        <Button 
          onClick={openCreate}
          className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold"
          data-testid="add-route-price-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Precio
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Buscar por origen o destino..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-white/5 border-white/10 text-white pl-10"
            data-testid="route-price-search"
          />
        </div>
        <Select value={filterOrigin} onValueChange={setFilterOrigin}>
          <SelectTrigger className="w-full sm:w-[280px] bg-white/5 border-white/10 text-white" data-testid="route-price-origin-filter">
            <SelectValue placeholder="Filtrar por origen" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E293B] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">Todos los orígenes</SelectItem>
            {origins.map(o => (
              <SelectItem key={o.id} value={o.id} className="text-white hover:bg-white/10">{o.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Routes Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-sm font-medium text-slate-400 px-4 py-3">Origen</th>
                <th className="text-left text-sm font-medium text-slate-400 px-4 py-3"></th>
                <th className="text-left text-sm font-medium text-slate-400 px-4 py-3">Destino</th>
                <th className="text-right text-sm font-medium text-slate-400 px-4 py-3">Precio (USD)</th>
                <th className="text-center text-sm font-medium text-slate-400 px-4 py-3">Estado</th>
                <th className="text-right text-sm font-medium text-slate-400 px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoutes.map((route) => (
                <tr key={route.id} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${!route.active && 'opacity-50'}`}>
                  <td className="px-4 py-3">
                    <span className="text-white text-sm font-medium">{zoneMap[route.origin_id]?.name || 'Desconocido'}</span>
                  </td>
                  <td className="px-2 py-3">
                    <ArrowRight className="w-4 h-4 text-cyan-400" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-white text-sm">{zoneMap[route.destination_id]?.name || 'Desconocido'}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-orange-400 font-bold text-lg">${route.price}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      route.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {route.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => openEdit(route)} data-testid={`edit-route-${route.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => setDeleteId(route.id)} data-testid={`delete-route-${route.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredRoutes.length === 0 && (
          <div className="p-12 text-center">
            <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No se encontraron precios de rutas</p>
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingRoute} onOpenChange={() => setEditingRoute(null)}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingRoute === 'new' ? 'Agregar Precio de Ruta' : 'Editar Precio de Ruta'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Origen (Aeropuerto/Puerto)</label>
              <Select value={formData.origin_id} onValueChange={(v) => setFormData({ ...formData, origin_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="route-origin-select">
                  <SelectValue placeholder="Seleccionar origen" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 max-h-60">
                  {origins.map(o => (
                    <SelectItem key={o.id} value={o.id} className="text-white hover:bg-white/10">{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Destino</label>
              <Select value={formData.destination_id} onValueChange={(v) => setFormData({ ...formData, destination_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="route-destination-select">
                  <SelectValue placeholder="Seleccionar destino" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 max-h-60">
                  {destinations.map(d => (
                    <SelectItem key={d.id} value={d.id} className="text-white hover:bg-white/10">{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Precio (USD)</label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="route-price-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-400">Activo</label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingRoute(null)} className="border-white/10 text-white hover:bg-white/5">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-route-price-btn">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar Precio de Ruta?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
